
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { RefreshCw, AlertTriangle } from "lucide-react";

export const SubscriptionFixer = () => {
  const { toast } = useToast();
  const [isFixing, setIsFixing] = useState(false);

  const handleFixSubscription = async () => {
    try {
      setIsFixing(true);
      console.log('Starting subscription fix...');
      
      const { data, error } = await supabase.functions.invoke('fix-subscription');
      
      if (error) {
        console.error('Error fixing subscription:', error);
        throw error;
      }

      console.log('Subscription fix result:', data);
      
      toast({
        title: "Subscription Synced!",
        description: `Your subscription has been synced from Stripe: ${data.subscription.type}`,
        className: "bg-green-50 border-green-200",
      });

      // Refresh the page to update the UI after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error: any) {
      console.error('Error fixing subscription:', error);
      toast({
        title: "Sync Failed",
        description: error.message || "Could not sync subscription from Stripe. Please contact support.",
        variant: "destructive",
      });
    } finally {
      setIsFixing(false);
    }
  };

  return (
    <Card className="border-blue-200 bg-blue-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-blue-800">
          <RefreshCw className="h-5 w-5" />
          Sync Subscription from Stripe
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-blue-700 mb-4">
          If your subscription type doesn't match what you purchased in Stripe, 
          click below to sync your subscription details from your Stripe account.
        </p>
        <Button 
          onClick={handleFixSubscription}
          disabled={isFixing}
          className="w-full bg-blue-600 hover:bg-blue-700"
        >
          {isFixing ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Syncing from Stripe...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Sync Subscription from Stripe
            </>
          )}
        </Button>
        <p className="text-xs text-blue-600 mt-2">
          This will check your Stripe account and update your subscription type to match your purchase.
        </p>
      </CardContent>
    </Card>
  );
};
